import { el, setAttr, setChildren } from 'redom';

import serverInterface from 'server-interface';

import { collectionToOptions } from 'generics/components/navigable-list/native/utilities';

import SingleValueSelector from '../single-value-selector';
import MultipleValuesSelector from '../multiple-values-selector';
import TextInput from '../text-input';
import DateInput from '../date-input';
import ToggleInput from '../toggle-input';

import inputTypes from '../operator-selector/supported-input-types';

class ValuesSelector {

  constructor(options) {

    this.optionsLoaded = false;
    this.fieldSelector = options.fieldSelector;
    this.options = [];
    this.criteria = options.criteria;
    this.onChange = options.onChange;
    this.onClose = options.onClose;

    this.input = null;
    this.initializeInput();

    this.el = el('.values-selector-main', [this.input]);

  }

  onmount() {

    this.loadOptions();

  }

  initializeInput() {

    const supportedInputType = this.criteria.operator.supportedInputType;

    if (supportedInputType === inputTypes.SINGLE) {
      this.initializeSingleInput();
    } else if (supportedInputType === inputTypes.MULTIPLE) {
      this.initializeMultipleInput();
    } else if (supportedInputType === inputTypes.TEXT) {
      this.initializeTextInput();
    } else if (supportedInputType === inputTypes.NUMBER) {
      this.initializeNumberTextInput();
    } else if (supportedInputType === inputTypes.DATE) {
      this.initializeDateTextInput();
    } else if (supportedInputType === inputTypes.DATE_TIME) {
      this.initializeDateTextInput();
    } else if (supportedInputType === inputTypes.TOGGLE) {
      this.initializeToggleInput();
    }

  }

  initializeSingleInput() {

    const options = {
      placeholder: 'Select value',
      field: this.criteria.field,
      value: this.criteria.value,
      options: this.options,
      onSelect: this.handleValueSelect.bind(this),
      onClose: this.handleInputClose.bind(this),
      optionsLoader: this.optionsLoader.bind(this)
    };

    this.input = new SingleValueSelector(options);

  }

  initializeMultipleInput() {

    const options = {
      placeholder: 'Select values',
      field: this.criteria.field,
      values: this.criteria.value,
      options: this.options,
      onSelect: this.handleValueSelect.bind(this),
      onClose: this.handleInputClose.bind(this),
      optionsLoader: this.optionsLoader.bind(this)
    };

    this.input = new MultipleValuesSelector(options);

  }

  initializeTextInput() {

    const options = {
      placeholder: 'Enter value',
      value: this.criteria.value,
      type: TextInput.types.TEXT,
      onChange: this.handleTextInputChange.bind(this),
      onClose: this.handleTextInputClose.bind(this),
      onEnterKey: this.handleTextInputClose.bind(this)
    };

    this.input = new TextInput(options);

  }

  initializeNumberTextInput() {

    const options = {
      placeholder: 'Enter value',
      value: this.criteria.value,
      type: TextInput.types.NUMBER,
      onChange: this.handleNumberTextInputChange.bind(this),
      onClose: this.handleNumberTextInputClose.bind(this),
      onEnterKey: this.handleNumberTextInputClose.bind(this)
    };

    this.input = new TextInput(options);

  }

  initializeDateTextInput() {

    const options = {
      placeholder: 'Select Date',
      value: this.criteria.value.key,
      type: this.criteria.operator.supportedInputType,
      onChange: this.handleDateTextInputChange.bind(this),
      onClose: this.handleDateTextInputClose.bind(this),
      onEnterKey: this.handleDateTextInputClose.bind(this)
    };

    this.input = new DateInput(options);

  }

  initializeToggleInput() {

    const options = {
      value: this.criteria.value.key,
      onChange: this.handleToggleInputChange.bind(this)
    };

    this.input = new ToggleInput(options);

  }

  tryUpdateInputType() {

    this.initializeInput();

    setChildren(this.el, [this.input]);

  }

  showOrHide() {

    if (this.criteria.operator.supportedTypes.indexOf(this.criteria.field.type) !== -1) {
      setAttr(this.el, { style: { display: 'block' } });
    } else {
      setAttr(this.el, { style: { display: 'none' } });
    }

  }

  setValues() {

    this.input.setValue(this.criteria.value);

  }

  onOperatorChange(criteria) {

    this.criteria = criteria;

    this.showOrHide();

    this.tryUpdateInputType();

    this.input.focus();

  }

  onFieldChange(criteria) {

    this.criteria = criteria;

    this.tryUpdateInputType();

    this.input.focus();

    this.loadOptions();

  }

  async loadOptions(forceLoad = false) {

    this.setWorking();

    this.options = await this.optionsLoader('');

    this.setIdle();

    this.setOptions(this.options);

  }

  handleValueSelect(value) {

    this.criteria.value = value;

    this.onChange(value);

  }

  handleInputClose() {

    this.onClose();

  }

  handleTextInputChange(value) {

    this.handleValueSelect({
      label: value,
      key: value
    });

  }

  handleTextInputClose() {}

  handleNumberTextInputChange(value) {

    const number = +value;

    this.handleValueSelect({
      label: number,
      key: number
    });

  }

  handleNumberTextInputClose() {}

  handleDateTextInputChange(value, date) {

    const datetime = date.getTime();

    this.handleValueSelect({
      label: datetime,
      key: datetime
    });

  }

  handleDateTextInputClose() {}

  handleToggleInputChange(status) {

    this.handleValueSelect({
      label: status,
      key: status
    });

  }

  setWorking() {

    if (this.criteria.operator.supportedInputType === inputTypes.SINGLE) {
      this.input.setWorking();
    } else if (this.criteria.operator.supportedInputType === inputTypes.MULTIPLE) {
      this.input.setWorking();
    }

  }

  setIdle() {

    if (this.criteria.operator.supportedInputType === inputTypes.SINGLE) {
      this.input.setIdle();
    } else if (this.criteria.operator.supportedInputType === inputTypes.MULTIPLE) {
      this.input.setIdle();
    }

  }

  setOptions() {

    if (this.criteria.operator.supportedInputType === inputTypes.SINGLE) {
      this.input.setOptions(this.options);
    } else if (this.criteria.operator.supportedInputType === inputTypes.MULTIPLE) {
      this.input.setOptions(this.options);
    }

  }

  async doLoadOptions(searchTerm) {

    const field = this.fieldSelector.getFieldByKey(this.criteria.field.key);;
    const fieldKey = field.key;
    if (fieldKey === 'userDefinedMessageLabel') {
      const labels = await serverInterface.startup.users.userSpaceConversationLabels.getUserSpaceConversationLabelsByQueryOptions({
        filterOptions: {
          name: searchTerm
        }
      });
      return collectionToOptions(labels, 'name', '_id');
    } else if (field.customLoaderAvailable === true) {
      return this.fieldSelector.customValuesLoader(field, searchTerm);
    } else {
      const userPropertyValues = await serverInterface.startup.analytics.getUserPropertyValuesByNameAndValue(fieldKey, searchTerm);
      return collectionToOptions(userPropertyValues, 'value', 'value');
    }

  }

  async optionsLoader(searchTerm) {

    const options = await this.doLoadOptions(searchTerm);

    return options;

  }

}

export default ValuesSelector;